/**
 * Who reports which error class. The declaration, not a description of one.
 *
 * ## Why this file exists (T806)
 *
 * Two reporters, each documented as relying on the other, and a class that fell
 * between them:
 *
 * - `app/components/route-error-boundary.tsx` skipped every `isRouteErrorResponse`
 *   because "those are already captured server-side by the loader/action".
 * - `app/services/observability-request.server.ts` skipped every `Response`
 *   because "a thrown Response is a redirect or a status response, never a fault".
 *
 * Both sentences were individually defensible. Together they meant a `400` that
 * left a member looking at the error screen — the T805 bug — reached Sentry from
 * neither side. Nobody lied; nobody had written down who the owner was. The
 * comment on the client had asserted a false invariant since T123.
 *
 * So the fix is not a better boolean in one of the two files. It is this table,
 * plus the rule that **neither reporter may hold its own opinion**: both derive
 * their guard from `serverReports()` / `boundaryReports()` below, and a comment
 * never carries an invariant across a module boundary on its own again
 * (R-T806-2).
 *
 * ## The table
 *
 * | Class                 | What it is                                     | Reporter |
 * |-----------------------|------------------------------------------------|----------|
 * | `delivered`           | a `Response`, `2xx` — a body thrown on purpose | nobody   |
 * | `redirect`            | a `Response`, `3xx`                            | nobody   |
 * | `expected-status`     | a `Response` in `CONTROL_FLOW_STATUSES`        | nobody   |
 * | `unexpected-status`   | a `Response`, any other `>= 400`               | server   |
 * | `server-fault`        | a non-`Response` thrown in a loader or action  | server   |
 * | `client-render-error` | a non-`Response` raised while rendering        | client   |
 *
 * ## Why the control-flow set is NAMED and not a threshold
 *
 * The spec declines `if (error instanceof Response && error.status < 400) return;`
 * and it is right to: a status code cannot distinguish control flow from fault. A
 * `404` for a member's mistyped URL is control flow; a `404` because our own code
 * built a bad URL is a bug, and it is the same number.
 *
 * A threshold smuggles that conclusion in as arithmetic. A named set states it, so
 * the next reader can disagree with a specific line instead of with a comparison
 * operator. Each entry below carries its own reason, and `root.tsx` already shipped
 * the same judgement in prose before this file existed ("a 404 or a 403 is a normal
 * answer, not an incident, and stamping an id on it would flood the channel").
 *
 * **The test for membership is "does our own code emit this on purpose?"** — not "is it
 * a 4xx". Every site in this tree that throws a `401`, `403`, `404`, `405`, `413` or
 * `429` is a deliberate guard answering *no*; none of them has a fault half.
 *
 * `400` is the one status where both meanings genuinely collide — the admin BO throws it
 * as a validation refusal, and T805's `400` was our own code building a bad request — and
 * it stays OUT of the set on purpose. That collision is the ticket's whole reason for
 * existing, and the fault half is the one a member sees. The cost is that a handful of
 * deliberate admin-BO refusals are reported; that is the trade, stated rather than
 * discovered.
 *
 * Measured on the tree that introduced this file — 112 `throw new Response` /
 * `throw data(...)` statements (plus 50 `throw redirect`, which never reach a boundary):
 * 78 are control flow (51 × `404`, 23 × `403`, one each of `401`, `405`, `413`, `429`),
 * 2 are `delivered` (the CSV downloads at `admin.leads.tsx:81` and
 * `admin.testimonials.tsx:102`), and 32 stay in the channel: 19 × `400` — where T805
 * lived — plus 7 × `503`, 4 × `502`, 2 × `500`. Re-measure by parsing each statement to
 * its balancing paren; a `grep -A<n>` window double-counts adjacent throws, which is how
 * an earlier version of this comment said 109.
 *
 * ## Declared residuals — read these before adding a case
 *
 * 1. **A `404` our own code caused is still unreported.** It is the spec's own
 *    example of a fault wearing a control-flow status, and this table cannot see
 *    the difference. Accepted deliberately, for the noise budget. The escape hatch
 *    is to throw a status outside the set at the site that knows better — not to
 *    widen the set, which would re-open the flood.
 * 2. **A `server-fault` CAN be reported twice** — see § Known gaps.
 *
 * ## Known gaps — what this table does NOT yet guarantee
 *
 * `server-fault` and `client-render-error` are one class to a boundary and cannot
 * be told apart there. React Router's `deserializeErrors` rebuilds a server-thrown
 * `Error` as a plain `Error`, so `isRouteErrorResponse` is false for it and the
 * boundary reports it — on top of the loader's own
 * `catch (e) { obs.captureError(userId, e); throw e; }`. That overlap predates T806
 * and is out of its scope by the spec's own `## Out of scope` line. It is declared
 * here, and pinned by a named test in `tests/unit/lib/error-ownership.test.ts`, so
 * the next reader finds it instead of rediscovering it. Recorded in T806
 * § Open after close.
 *
 * **How often it actually fires is UNKNOWN, and an earlier version of this comment
 * over-claimed it.** Measured against production Sentry on 2026-09-22, 90-day window:
 *
 *   * The mechanism is reachable from **18** `obs.captureError(…); throw e;` sites.
 *     The other **19** capture-then-`return` — they degrade, so their error never
 *     reaches a boundary and no client leg can exist. The highest-volume server
 *     reporter, `($lang)._index/loader` (284 events), is one of the degrading ones,
 *     which is why the pairing is invisible at the top of the counts.
 *   * The client leg IS in production: issue `JAVASCRIPT-REACT-A`, message
 *     `Error: Unexpected Server Error` (react-router's sanitised text), `source:client`,
 *     `boundary:root`, **36 occurrences since 2026-06-19**.
 *   * But **no sampled incident carried both legs under one `incident_id`.** Four
 *     incidents on the rethrowing `($lang).dashboard` / `($lang).library` paths
 *     (`K28L-88DR-RSTV`, `D28X-WH8Z-XQLA`, `B85Z-YJX5-HVLW`, `CA24-7YBJ-EGY8`) each
 *     carry exactly one `source:server` event and no client twin.
 *
 * So: real mechanism, present client leg, **pairing unproven**. Whoever opens the
 * follow-up ticket should measure the rate before sizing the fix — do not inherit the
 * word "twice" from this file.
 *
 * `root.tsx` also emits `error_page_5xx_rendered` for any boundary render at
 * `>= 500`. That is a **surface** event — "a member was shown the failure page" —
 * not a fault report, it is fingerprinted separately, and it is deliberately
 * outside this table. Said out loud so a future reader does not count it as a
 * second reporter and "fix" it.
 */

/** The error classes this repo reports on. Exactly one reporter each, below. */
export type ErrorClass =
  | "delivered"
  | "redirect"
  | "expected-status"
  | "unexpected-status"
  | "server-fault"
  | "client-render-error";

/** Who files the Sentry event. `nobody` is a decision, not an omission. */
export type Reporter = "server" | "client" | "nobody";

/**
 * The single declaration. Both reporters derive their guard from this — neither
 * one repeats a status number or an `instanceof` opinion of its own.
 */
export const ERROR_OWNERSHIP: Readonly<Record<ErrorClass, Reporter>> = {
  // A body delivered by throwing — the download-from-a-loader pattern, which returns
  // its CSV by throwing a 200 rather than by returning loader data. It succeeded and it
  // never reaches a boundary. Without this class a 200 falls through to
  // `unexpected-status` and the server files a successful download as an incident:
  // `UnknownError: [object Response]` with a better name, which is the noise this whole
  // file exists to remove. Found by AI review on PR #1818.
  delivered: "nobody",
  // React Router throws a Response to redirect. Filing one as an incident files
  // navigation as a fault; 19 of the 43 pre-T38 call sites already knew this.
  redirect: "nobody",
  // The request was answered correctly and the answer is no. See the set below.
  "expected-status": "nobody",
  // She is looking at the error screen and something we did not plan put her
  // there. The server owns it: it holds the userId, the scrubbed stack and the
  // incidentId that joins this event to `X-Request-Id` (T38). The client does
  // not — root mints an incident id only for 5xx.
  "unexpected-status": "server",
  // Unchanged by T806. The loader's own catch reports it; the boundary may report it
  // again, at a rate nobody has measured — see § Known gaps.
  "server-fault": "server",
  // The server never saw it: it happened after hydration, in a component.
  "client-render-error": "client",
};

/**
 * Statuses that mean "answered, and the answer is no" — control flow, not fault.
 * One line per entry, because the reason is the point of the set.
 */
export const CONTROL_FLOW_STATUSES: ReadonlySet<number> = new Set([
  401, // not signed in — the route is doing its job
  403, // signed in, not allowed (the admin gate, the read-only environment guard)
  404, // the thing is not there; her typo and our bad link are the same number
  405, // wrong method on a method-gated route (`api.user.timezone.tsx:37`)
  413, // her avatar is over 10 MB (`images.server.ts:31`) — the limit answering, not failing
  429, // the rate limiter working as designed (`media.$.tsx:163`). The flood risk here is
  //      the worst of the six: under a credential-stuffing or scraping burst this is one
  //      event per REJECTED request, so reporting it would let an attacker choose how
  //      loud our alerting gets. Added on AI review of PR #1818.
]);

/** The status a thrown `Response` carries, or `undefined` for anything else. */
export function httpStatusOf(error: unknown): number | undefined {
  return error instanceof Response ? error.status : undefined;
}

function classifyStatus(status: number): ErrorClass {
  // Ordered low to high so nothing falls through to the `unexpected-status` default by
  // accident — which is exactly how a thrown 2xx became a reported fault in the first
  // version of this file.
  if (status < 300) return "delivered";
  if (status < 400) return "redirect";
  if (CONTROL_FLOW_STATUSES.has(status)) return "expected-status";
  return "unexpected-status";
}

/**
 * What the SERVER sees — the value as it was thrown, before React Router
 * serialises anything. A real `Response` here is the thrown Response itself.
 */
export function classifyThrown(error: unknown): ErrorClass {
  const status = httpStatusOf(error);
  return status === undefined ? "server-fault" : classifyStatus(status);
}

/**
 * What a BOUNDARY sees — a React Router `ErrorResponse` (a plain object with a
 * numeric `status`, not a `Response` instance) or anything else.
 *
 * Structural rather than `isRouteErrorResponse`, so this module stays free of a
 * react-router import and remains a pure `app/lib/` unit. The four conditions are
 * react-router's own, character for character (`isRouteErrorResponse`, v7.18.3) —
 * a looser test here would be a second opinion about the same value, which is the
 * exact failure this file exists to end.
 */
export function classifyAtBoundary(error: unknown): ErrorClass {
  if (error == null || typeof error !== "object" || !("data" in error)) return "client-render-error";
  const e = error as { status?: unknown; statusText?: unknown; internal?: unknown };
  if (
    typeof e.status !== "number" ||
    typeof e.statusText !== "string" ||
    typeof e.internal !== "boolean"
  ) {
    return "client-render-error";
  }
  return classifyStatus(e.status);
}

/** Does `observeRequest.captureError` file this one? Derived, never re-decided. */
export function serverReports(error: unknown): boolean {
  return ERROR_OWNERSHIP[classifyThrown(error)] === "server";
}

/**
 * Does a route or root error boundary file this one? Derived, never re-decided.
 *
 * Note the asymmetry this encodes: a boundary reports nothing that arrived as a
 * status response, because all three of those classes are owned elsewhere (or by
 * nobody). What it does report is everything that did not — which is the class it
 * is the only witness to, plus the `server-fault` overlap in § Known gaps.
 */
export function boundaryReports(error: unknown): boolean {
  return ERROR_OWNERSHIP[classifyAtBoundary(error)] === "client";
}
